import { FormEvent, useEffect, useState } from "react";
import {
  GetJobworkDetailByJoNoAPI,
  GetJoStatusMasterAPI,
} from "../../../db/apiCalls";
import { toast } from "react-toastify";
import { JoDetailType, JoStatusType } from "../../../db/model";
import StatusUpdateDetailCard from "./StatusUpdateDetailCard";
import CachedIcon from "@mui/icons-material/Cached";
import ImageModal from "../../custom-image-modal/ImageModal";
import { useCustomImageModal } from "../../custom-image-modal/useCustomImageModal";
import ConfirmModal from "../../custom-confirm-box/ConfirmModal";
import { useCustomConfirm } from "../../custom-confirm-box/useCustomConfirm";
import BarcodeScannerComponent from "react-qr-barcode-scanner";

import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import ErrorBoundary from "../../../ErrorBoundary";

import FlashlightOnIcon from "@mui/icons-material/FlashlightOn";
import FlashlightOffIcon from "@mui/icons-material/FlashlightOff";

function StatusUpdate() {
  // image modal
  const {
    isImageModalOpen,
    customImageModal,
    handleImageModalClose,
    image,
    remarks,
    joNo,
    status,
  } = useCustomImageModal();

  // custom confirm box
  const {
    isOpen,
    message,
    customConfirm,
    handleCancel,
    handleConfirm,
    confirmText,
  } = useCustomConfirm();

  const [joStatusList, setJoStatusList] = useState<JoStatusType[]>([]);

  async function getJoStatusMaster() {
    const result = await GetJoStatusMasterAPI();

    const parsedResponse = result
      ? JSON.parse(result)
      : {
          statusCode: 0,
          message: "Please check your internet connection!",
          success: false,
          status: [],
        };

    if (parsedResponse.success) {
      return parsedResponse.status;
    } else {
      toast.error(`Error getting JO status, ${parsedResponse.message}`);
      return [];
    }
  }

  useEffect(() => {
    getJoStatusMaster().then((status) => setJoStatusList(status));
  }, []);

  const [joNoState, setJoNoState] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [joDetailList, setJoDetailList] = useState<JoDetailType[]>([]);

  async function getJobworkDetailByJoNo(joNo = joNoState) {
    setIsLoading(true);

    if (!joNo) {
      setJoDetailList([]);
      toast.warning("Please enter JO No.");
    } else {
      const result = await GetJobworkDetailByJoNoAPI(joNo);

      const parsedResponse = result
        ? JSON.parse(result)
        : {
            statusCode: 0,
            message: "Please check your internet connection!",
            success: false,
            poDetails: [],
          };

      if (parsedResponse.success) {
        // setJoDetailList(addIsDifferentProperty(parsedResponse.poDetails));
        setJoDetailList(parsedResponse.poDetails);
        if (parsedResponse.poDetails.length < 1) {
          toast.error("No Jobwork details found for the given Job No");
        }
      } else {
        toast.error(`Error getting JO details, ${parsedResponse.message}`);
        setJoDetailList([]);
      }
    }

    setIsLoading(false);
  }

  // Initialize audio for the beep sound effect
  // const beepSound = new Audio("../../assets/sound/beep.mp3");

  const [isScanning, setIsScanning] = useState(false);

  const [flashlightState, setFlashlightState] = useState(false);

  const scanBarOrQRCode = (_err: any, result: any) => {
    if (result) {
      // Update current data and add it to the scan history
      setJoNoState(result.text);

      // Play the beep sound for successful scan feedback
      // beepSound.play();

      // Show a success notification using toast
      toast.success(`Product scanned: ${result.text}`);

      getJobworkDetailByJoNo(result.text);

      setIsScanning(false);
    } else {
      // setJoNoState(""); // Set message if no result
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen w-full pb-12">
        <form
          className="flex gap-x-4 py-2 mx-3 rounded-b-lg md:justify-center sticky top-0 backdrop-blur-lg"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            getJobworkDetailByJoNo();
          }}
        >
          <div className="block bg-white w-full rounded-md border-0 p-0.5 text-gray-900 ring-1 ring-inset ring-gray-300 group-focus:ring-2 group-focus:ring-inset group-focus:outline group-focus:ring-indigo-600 sm:text-sm sm:leading-6 md:max-w-[25%] my-auto">
            <input
              type="text"
              name="joNo"
              id="joNo"
              value={joNoState}
              onChange={(e) => setJoNoState(e.target.value)}
              placeholder="JO No."
              className="group placeholder:text-gray-400 bg-white rounded-md px-1 py-1 w-10/12 border-0 ring-0 focus:ring-0 focus:border-0 focus:outline-none"
            />

            <button
              className="w-2/12"
              type="button"
              onClick={() => setIsScanning((prev) => !prev)}
            >
              {isScanning ? <NoPhotographyIcon /> : <QrCodeScannerIcon />}
            </button>
          </div>

          {isScanning && (
            <button
              type="button"
              className="bg-white py-1 px-1.5 rounded-md border border-gray-300"
              onClick={() => setFlashlightState((prev) => !prev)}
            >
              {flashlightState ? <FlashlightOffIcon /> : <FlashlightOnIcon />}
            </button>
          )}

          <button
            className="bg-blue-200 hover:bg-blue-300 py-1 px-2 rounded-md"
            type="submit"
          >
            Search
          </button>
        </form>

        {/* Barcode scanner component */}
        {isScanning && (
          <div className="max-w-max mx-auto my-2">
            <BarcodeScannerComponent
              width={250}
              height={250}
              torch={flashlightState}
              facingMode={"environment"}
              onUpdate={scanBarOrQRCode}
              onError={(error: any) => {
                if (error.name === "NotAllowedError") {
                  // Handle messaging in our app after the user chooses to not allow the camera permissions
                  toast.error(
                    "Camera access permission is required to scan QR!"
                  );
                }
                setIsScanning(false);
              }}
              delay={1500} // 1.5 seconds delay between scans
            />
          </div>
        )}

        <div className="p-2">
          {isLoading ? (
            <div className="flex h-screen text-center items-center justify-center">
              <CachedIcon className="animate-spin" fontSize="large" />
            </div>
          ) : (
            <div className="flex flex-col gap-y-2 px-2">
              {/* cards */}
              {joDetailList.map((joDetail, index, arr) => (
                <>
                  {index !== 0 &&
                    joDetail.orderNo !== arr[index - 1].orderNo && (
                      <div
                        className="h-0 w-full my-2 border-b-2 border-red-600"
                        key={`divide-${index}`}
                      ></div>
                    )}
                  <StatusUpdateDetailCard
                    key={index}
                    joStatusList={joStatusList}
                    joDetail={joDetail}
                    customImageModal={customImageModal}
                    getJobworkDetailByJoNo={getJobworkDetailByJoNo}
                    customConfirm={customConfirm}
                  />
                </>
              ))}
            </div>
          )}
        </div>

        {/* confirm box */}

        <ConfirmModal
          isOpen={isOpen}
          message={message}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          confirmText={confirmText}
        />

        {/* image modal */}

        <ImageModal
          isImageModalOpen={isImageModalOpen}
          handleImageModalClose={handleImageModalClose}
          joNo={joNo}
          status={status}
          image={image}
          remarks={remarks}
        />
      </div>
    </ErrorBoundary>
  );
}

export default StatusUpdate;
